import type { AdminAppealListItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AppealsFilterBar } from './_components/AppealsFilterBar';
import { buildAppealColumns } from './_lib/appealColumns';
import { appealFiltersSchema, hasActiveFilters } from './_lib/appealFilters';
import { AppealDetailDrawer } from './AppealDetailDrawer';
import { useAdminAppealsList } from '../../api/admin-appeals';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/appeals` — admin appeals moderation list (AUT-130 / ADMIN-18). The
 * EIGHTH admin vertical.
 *
 * Gated on `resolve_appeals` (the controller-level permission). PII
 * columns (customer name) render via `<RedactedField perm="view_pii">`,
 * which agrees with the server's `piiRevealed` flag (the dual inspector
 * identity lands on the detail drawer).
 *
 * Filters + pagination are URL-state-driven via `useUrlFilters`; cursor
 * pagination bridges the cursor backend to the page-based DataGrid via
 * `useCursorPagination`. Row click opens the 720px detail drawer.
 *
 * Clones `ReportsListPage` shape — same primitive stack, same observer
 * wrap, same filter-reset-on-change effect.
 */
const AppealsListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(appealFiltersSchema);
  const pager = useCursorPagination();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Any filter change resets pagination to the first page (the cursor
  // space is only valid for the filter set that produced it).
  const filterKey = `${(filters.status ?? []).join(',')}|${(filters.priority ?? []).join(',')}|${
    filters.dateFrom ?? ''
  }|${filters.dateTo ?? ''}`;
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable (useCallback); re-run only on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminAppealsList({
    ...(filters.status && filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.priority && filters.priority.length > 0 ? { priority: filters.priority } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  // Feed the latest nextCursor into the pager once each fetch settles.
  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildAppealColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('appeals.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('appeals.subtitle')}
        </Typography>
      </Box>

      <AppealsFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminAppealListItemDto>
        rows={items}
        columns={columns}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => setSelectedId(row.id)}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('appeals.empty')}
          </Typography>
        }
      />

      <AppealDetailDrawer appealId={selectedId} onClose={() => setSelectedId(null)} />
    </Box>
  );
};

const Observed = observer(AppealsListPageInner);

/** Whole-page gate: no `resolve_appeals` → the default 403 panel. */
export const AppealsListPage = (): ReactElement => (
  <RequirePermission perm="resolve_appeals">
    <Observed />
  </RequirePermission>
);
